import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Calendar, DollarSign, TrendingUp, Clock, Eye, Users, MessageCircle, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    revenue: 0,
  });
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchAnalytics();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      const bookingsData = response.data;
      setBookings(bookingsData);

      const total = bookingsData.length;
      const pending = bookingsData.filter((b) => b.status === "pending").length;
      const confirmed = bookingsData.filter((b) => b.status === "confirmed").length;
      const revenue = bookingsData
        .filter((b) => b.status === "confirmed")
        .reduce((sum, b) => sum + b.total_price, 0);

      setStats({ total, pending, confirmed, revenue });
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      confirmed: "bg-green-100 text-green-800 border-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      <Badge className={variants[status] || ""} data-testid={`badge-${status}`}>
        {status === "pending" ? "Í bið" : status === "confirmed" ? "Staðfest" : "Afbókuð"}
      </Badge>
    );
  };

  const maxDailyViews = analytics?.daily ? Math.max(...analytics.daily.map(d => d.views), 1) : 1;

  return (
    <AdminLayout>
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight mb-8" data-testid="dashboard-title">
          Yfirlit
        </h1>

        {/* Visitor Analytics */}
        {analytics && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" /> Heimsóknir
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="stat-views-today">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5 text-blue-500" />
                  <span className="text-xs font-ui text-muted-foreground uppercase tracking-wider">Í dag</span>
                </div>
                <div className="text-2xl font-display font-bold">{analytics.today_views}</div>
                <div className="text-xs text-muted-foreground">{analytics.unique_today} einstakir</div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="stat-views-week">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5 text-indigo-500" />
                  <span className="text-xs font-ui text-muted-foreground uppercase tracking-wider">Vika</span>
                </div>
                <div className="text-2xl font-display font-bold">{analytics.week_views}</div>
                <div className="text-xs text-muted-foreground">{analytics.unique_week} einstakir</div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="stat-views-month">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  <span className="text-xs font-ui text-muted-foreground uppercase tracking-wider">Mánuður</span>
                </div>
                <div className="text-2xl font-display font-bold">{analytics.month_views}</div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="stat-contacts">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-5 h-5 text-orange-500" />
                  <span className="text-xs font-ui text-muted-foreground uppercase tracking-wider">Ólesnar fyrirspurnir</span>
                </div>
                <div className="text-2xl font-display font-bold">{analytics.unread_contacts}</div>
              </div>
            </div>

            {/* Mini bar chart */}
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <h3 className="text-sm font-ui font-medium text-muted-foreground mb-3">Heimsóknir síðustu 14 daga</h3>
              <div className="flex items-end gap-1 h-24" data-testid="analytics-chart">
                {analytics.daily.map((day, i) => (
                  <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-primary/80 rounded-t hover:bg-primary transition-colors"
                      style={{ height: `${Math.max((day.views / maxDailyViews) * 80, 2)}px` }}
                      title={`${day.date}: ${day.views} heimsóknir`}
                    ></div>
                    {i % 2 === 0 && (
                      <span className="text-[9px] text-muted-foreground">{day.date.slice(5)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Booking Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="stat-total-bookings">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="w-8 h-8 text-primary" />
              <span className="text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                Bókanir
              </span>
            </div>
            <div className="text-3xl font-display font-bold text-foreground">{stats.total}</div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="stat-pending">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <span className="text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                Í bið
              </span>
            </div>
            <div className="text-3xl font-display font-bold text-foreground">{stats.pending}</div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="stat-confirmed">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <span className="text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                Staðfestar
              </span>
            </div>
            <div className="text-3xl font-display font-bold text-foreground">{stats.confirmed}</div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="stat-revenue">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="w-8 h-8 text-primary" />
              <span className="text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">
                Tekjur
              </span>
            </div>
            <div className="text-3xl font-display font-bold text-foreground">€{stats.revenue.toFixed(0)}</div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-2xl font-heading font-semibold mb-6" data-testid="recent-bookings-title">
            Nýlegar bókanir
          </h2>
          {bookings.length === 0 ? (
            <p className="text-muted-foreground font-body text-center py-8">Engar bókanir enn</p>
          ) : (
            <div className="overflow-x-auto" data-testid="recent-bookings-table">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">Gestur</th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">Inn</th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">Út</th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">Gestir</th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">Verð</th>
                    <th className="text-left py-3 px-4 text-sm font-ui font-medium text-muted-foreground uppercase tracking-wider">Staða</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.slice(0, 10).map((booking) => (
                    <tr key={booking.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`booking-row-${booking.id}`}>
                      <td className="py-4 px-4 font-body">{booking.guest_name}</td>
                      <td className="py-4 px-4 font-body">{booking.check_in}</td>
                      <td className="py-4 px-4 font-body">{booking.check_out}</td>
                      <td className="py-4 px-4 font-body">{booking.num_guests}</td>
                      <td className="py-4 px-4 font-body font-semibold">€{booking.total_price.toFixed(0)}</td>
                      <td className="py-4 px-4">{getStatusBadge(booking.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
